import { toDayJs } from '~/services/TimeService';

import type { ISODateRange } from '~/types/stopsell';

import { mergeDateRanges } from '~/utils/mergeDateRanges';

import { isIsoDate } from '../blackouts/blackoutsValidation';
import type { BlackoutDraft } from '../blackouts/types';

export interface AvailableTravelDays {
  blackedOutDays: number;
  availableDays: number;
}

// Allocation__c is labelled "Allocation Per Day", so the denominator counts days a room can be
// held, not nights stayed. countNights is end-exclusive and would drop one day from every range.
const countDaysInclusive = (start: string, end: string): number => toDayJs(end).diff(toDayJs(start), 'day') + 1;

const clipToWindow = (range: ISODateRange, from: string, to: string): ISODateRange | undefined => {
  const start = range.start < from ? from : range.start;
  const end = range.end > to ? to : range.end;

  return start > end ? undefined : { start, end };
};

export function computeAvailableTravelDays(
  travelFrom: string | null | undefined,
  travelTo: string | null | undefined,
  blackouts: Array<BlackoutDraft>,
): AvailableTravelDays | null {
  if (!travelFrom || !travelTo || !isIsoDate(travelFrom) || !isIsoDate(travelTo) || travelFrom > travelTo) {
    return null;
  }

  const clipped = blackouts.flatMap((blackout) => {
    if (!isIsoDate(blackout.startDate) || !isIsoDate(blackout.endDate) || blackout.startDate > blackout.endDate) {
      return [];
    }

    // A blackout may run past either edge of the window. Subtracting it whole would shrink the
    // denominator and raise a materialisation alarm for days the offer never sold.
    const inWindow = clipToWindow({ start: blackout.startDate, end: blackout.endDate }, travelFrom, travelTo);

    return inWindow ? [inWindow] : [];
  });

  const blackedOutDays = mergeDateRanges(clipped).reduce(
    (total, range) => total + countDaysInclusive(range.start, range.end),
    0,
  );

  return {
    availableDays: Math.max(0, countDaysInclusive(travelFrom, travelTo) - blackedOutDays),
    blackedOutDays,
  };
}
